import { BreakpointObserver } from '@angular/cdk/layout';
import { HttpClient } from '@angular/common/http';
import { Component, inject, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import {
    AuthService,
    BlockAbstractComponent,
    PageService,
    ParserService,
    SSOService,
} from '@gallaghercp/core-frontend';

@Component({
    selector: 'core-splash-page',
    templateUrl: './block-splash-landing-page.component.html',
    styleUrls: ['./block-splash-landing-page.scss'],
    standalone: false,
})
export class BlockSplashLandingComponent extends BlockAbstractComponent implements OnInit {
    protected override parser: ParserService;
    protected override pages: PageService;
    protected override auth: AuthService;
    protected breakpoint = inject(BreakpointObserver);
    protected http = inject(HttpClient);
    protected sso = inject(SSOService);
    protected router = inject(Router);

    backgroundImage!: string;
    mobileBackgroundImage!: string;
    isMobile: boolean = false;
    isNoButtons: boolean = false;
    showStdLink: boolean = false;
    showStdLink2: boolean = false;
    showSsoLink: boolean = false;
    showSsoLink2: boolean = false;

    constructor() {
        const parser = inject(ParserService);
        const pages = inject(PageService);
        const auth = inject(AuthService);

        super(parser, auth, pages);

        this.parser = parser;
        this.pages = pages;
        this.auth = auth;
    }

    override ngOnInit() {
        super.ngOnInit();
        this.initializeBackgroundImage();
        this.initializeLinkVisibility();
        this.evaluateButtonState();

        this.subscriptions.push(
            this.breakpoint.observe(['(max-width: 991px)']).subscribe(state => {
                this.isMobile = state.matches;
            }),

            this.auth.authChangeEmit.subscribe(() => {
                const zoneRouting = this.configService.get('zoneRouting') as any[];
                const redirectRoute = zoneRouting?.find((zr: any) => zr.id === this.auth.getUser()?.zone)?.route;
                if (redirectRoute && this.router.url !== redirectRoute) {
                    this.router.navigate([redirectRoute]);
                }
            }),
        );
    }

    getContainerStyle() {
        const styles: Record<string, string> = {};

        const ctaWidth: unknown = this.block?.data?.containerWidth;
        if (typeof ctaWidth === 'string' && ctaWidth.trim().length > 0) {
            styles['max-width'] = ctaWidth.trim(); // expects rem values from CMS
        }

        const splashConfig = this.configService.get('splashLandingPage') as any;
        const ctaTopMargin = splashConfig?.['splashCtaMargin'];
        if (ctaTopMargin) {
            styles['margin'] = ctaTopMargin;
        }

        return styles;
    }

    getHeadingStyle() {
        const styles: Record<string, string> = {};

        const size: unknown = this.block?.data?.headingFontSize;
        if (typeof size === 'string' && size.trim().length > 0) {
            styles['font-size'] = size.trim();
        }

        const color: unknown = this.block?.data?.headingFontColor;
        if (typeof color === 'string' && color.trim().length > 0) {
            styles['color'] = color.trim();
        }

        const weight: unknown = this.block?.data?.headingFontWeight;
        if (typeof weight === 'number') {
            styles['font-weight'] = weight.toString();
        }

        return styles;
    }

    getTextStyle() {
        const styles: Record<string, string> = {};

        const size: unknown = this.block?.data?.introFontSize;
        if (typeof size === 'string' && size.trim().length > 0) {
            styles['font-size'] = size.trim();
        }

        const color: unknown = this.block?.data?.introFontColor ?? this.block?.data?.textFontColor;
        if (typeof color === 'string' && color.trim().length > 0) {
            styles['color'] = color.trim();
        }

        const mb: unknown = this.block?.data?.introBottomMarginRem;
        if (typeof mb === 'string' && mb.trim().length > 0) {
            styles['margin-bottom'] = mb.trim(); // expects rem values from CMS
        }

        return styles;
    }

    getSplashBackground() {
        const styles: any = {
            'background-image': 'none',
        };

        if (this.block?.data?.backgroundImageRightStacked) {
            if (this.block?.data?.backgroundColour) {
                styles['background-color'] = this.block.data.backgroundColour;
            }
            return styles;
        }

        if (this.block?.data?.backgroundChoice === 'Image') {
            let bg = this.backgroundImage;

            if (this.isMobile && this.mobileBackgroundImage) {
                bg = this.mobileBackgroundImage;
            }

            if (bg) {
                styles['background-image'] = `url(${bg})`;
                styles['background-color'] = null;
                styles['background-size'] = 'cover';
                return styles;
            }
        }

        if (this.block?.data?.backgroundChoice === 'Colour' && this.block?.data?.backgroundColour) {
            styles['background-color'] = this.block.data.backgroundColour;
        }

        return styles;
    }

    getRightStackedBackgroundStyle() {
        if (!this.block?.data?.backgroundImageRightStacked || this.block?.data?.backgroundChoice !== 'Image') {
            return { display: 'none' };
        }

        let bg = this.backgroundImage;

        if (this.isMobile && this.mobileBackgroundImage) {
            bg = this.mobileBackgroundImage;
        }

        if (bg) {
            return {
                'background-image': `url(${bg})`,
                'background-size': 'cover',
                'background-position': 'center',
            };
        }

        return { display: 'none' };
    }

    mobileBackgroundClass() {
        if (this.block?.data?.mobileImage && this.isMobile) {
            return 'mobile-background';
        } else {
            return '';
        }
    }

    get hasMobileOverlay(): boolean {
        if (!this.isMobile) return false;
        const splashConfig = this.configService.get('splashLandingPage') as any;
        return !!splashConfig?.['addOverlayToSplashForMobile'];
    }

    // This needs testing when we have an SSO to use
    initiateSSO(sso: string) {
        this.subscriptions.push(this.sso.initiate(sso, 'default', this.block.data.relayStateUrl || null).subscribe());
    }

    private initializeBackgroundImage(): void {
        if (this.block.data.backgroundChoice === 'Image' && this.block.data.image_url) {
            this.backgroundImage = this.block.data.image_url;
        }

        if (this.block.data.mobileImage) {
            this.mobileBackgroundImage = this.block.data.mobileImage;
        }
    }

    private initializeLinkVisibility(): void {
        this.showStdLink = this.shouldShowStandardLink();
        this.showStdLink2 = this.shouldShowStandardLink2();
        this.showSsoLink = this.shouldShowSsoLink();
        this.showSsoLink2 = this.shouldShowSsoLink2();
    }

    private shouldShowStandardLink(): boolean {
        return !!(this.block.data.linkText && this.block.data.link && !this.block.data.ssoLink);
    }

    private shouldShowStandardLink2(): boolean {
        return !!(this.block.data.linkText2 && this.block.data.link2 && !this.block.data.ssoLink2);
    }

    private shouldShowSsoLink(): boolean {
        return !!(this.block.data.linkText && this.block.data.ssoLink);
    }

    private shouldShowSsoLink2(): boolean {
        return !!(this.block.data.linkText2 && this.block.data.ssoLink2);
    }

    private evaluateButtonState(): void {
        // If we have CTA items in content, evaluate based on them; otherwise fall back to legacy flags
        const hasContentCtas = this.hasAnyCtasInContent();
        if (hasContentCtas !== null) {
            this.isNoButtons = !hasContentCtas;
        } else {
            this.isNoButtons = !this.showStdLink && !this.showStdLink2 && !this.showSsoLink && !this.showSsoLink2;
        }
    }

    /**
     * Returns true if any CTA item in block.content is renderable, false if none are renderable,
     * and null if content is not present (so caller can fall back to legacy logic).
     */
    private hasAnyCtasInContent(): boolean | null {
        const items: any[] | undefined = (this.block as any)?.content;
        if (!Array.isArray(items) || items.length === 0) {
            return null;
        }

        // Renderable if it has text + (link or sso), considering 1st and optional 2nd button
        const canRender = (it: any): boolean => {
            const data = (it && it.data) || {};
            const hasPrimaryCta = !!(data.linkText && (data.ssoLink || data.link));
            const hasSecondaryCta = !!(data.isTwoButtons && data.linkText2 && (data.ssoLink2 || data.link2));
            return hasPrimaryCta || hasSecondaryCta;
        };

        return items.some(canRender);
    }

    // Safe accessor for template to avoid optional chaining undefined checks
    get contentItems(): any[] {
        const items: any = (this.block as any)?.content;
        return Array.isArray(items) ? items : [];
    }
}
